import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormHelperText, FormLabel,
    Grid, IconButton, Radio, RadioGroup, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { LoadingButton } from '@mui/lab';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useSnackbar } from 'notistack';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { HOST } from 'app/utils/constants/paths';
import _ from 'lodash';
import orderServices from 'app/services/order-services';
import printerServices from 'app/services/printer-services';
import qzPrintServices from 'app/services/qz-print-services';
import { REGIONES, COMUNAS, regionDeComuna, comunaCanonica } from 'app/utils/constants/chile';
import { DEFAULT_DROPOFF_CARRIER_CATALOG } from 'app/utils/dropoffCarriers';

// Formatea mientras se tipea: 12600132-0 -> 12.600.132-0
const formatearRut = (valor) => {
    const limpio = String(valor || '').replace(/[^0-9kK]/g, '').toUpperCase();
    if (limpio.length < 2) return limpio;
    return `${limpio.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${limpio.slice(-1)}`;
};

// Módulo 11, el mismo dígito verificador que usa el SII. Evita mandar a imprimir
// una etiqueta con un RUT mal tipeado, que es imposible de corregir después.
const rutValido = (valor) => {
    const limpio = String(valor || '').replace(/[^0-9kK]/g, '').toUpperCase();
    if (limpio.length < 8) return false;
    const cuerpo = limpio.slice(0, -1);
    let suma = 0;
    let factor = 2;
    for (let i = cuerpo.length - 1; i >= 0; i -= 1) {
        suma += parseInt(cuerpo[i], 10) * factor;
        factor = factor === 7 ? 2 : factor + 1;
    }
    const resto = 11 - (suma % 11);
    const esperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
    return limpio.slice(-1) === esperado;
};

// Mismas reglas que Zebra::HaulersShippingLabelZpl.formatear_telefono, para que
// la vista previa diga exactamente lo que va a salir impreso.
const formatearTelefono = (tel) => {
    const d = String(tel || '').replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('56')) return `+56 ${d[2]} ${d.slice(3, 7)} ${d.slice(7)}`;
    if (d.length === 9) return `+56 ${d[0]} ${d.slice(1, 5)} ${d.slice(5)}`;
    return tel || '';
};

const PAGOS = { pagado: 'PAGADO', por_pagar: 'POR PAGAR' };

// Largo máximo de lo que se tipea: lo que cabe en la etiqueta 10×15. El preflight
// trae los mismos números del backend (Labels::HaulersLabel::LIMITES); estos son
// solo el respaldo mientras carga.
const LIMITES = { nombre: 60, direccion: 100, comentario: 100 };

const CAMPOS_REMITENTE = [
    { key: 'nombre', label: 'Nombre o razón social' },
    { key: 'rut', label: 'RUT' },
    { key: 'direccion', label: 'Dirección' },
    { key: 'comuna', label: 'Comuna' },
    { key: 'telefono', label: 'Teléfono' },
    { key: 'email', label: 'Email' },
];

// Réplica de la etiqueta con los mismos datos que van al ZPL. No es pixel exacto
// —el ZPL lo rasteriza la impresora— pero respeta el orden y las proporciones,
// que es lo que sirve para cachar una dirección sin número antes de gastar papel.
// Igual que en el ZPL, lo que viene vacío no aparece.
// Misma cuenta que bloque_destinatario en Zebra::HaulersShippingLabelZpl, en dots del
// papel 10×15: cada campo achica su fuente hasta caber en sus líneas (o se corta con
// "…"), y si el bloque no entra sobre la línea del bulto se achica todo parejo. Así
// la vista previa muestra el mismo tamaño y el mismo corte que va a salir impreso.
const ANCHO_TEXTO = 703;
const ANCHO_CARACTER = 0.58;
const ANCHO_CARACTER_LINEAS = 0.53;
const FUENTE_MIN = 22;
const QUIEBRE = 6;
const ESCALA_MIN = 0.6;
const Y_LINEA_BULTO = 930;
// La línea bajo el encabezado va entre 150 y 330 según el logo y el remitente; con
// un logo de alto completo cae en ~262. Se toma ese, que deja la previa del lado de
// achicar de más y no de menos.
const Y_LINEA_DESTINATARIO = 262;
// px de la vista previa por dot: 210 px de ancho para 799 dots.
const PX_POR_DOT = 210 / 799;

const CAMPOS_PREVIA = [
    ['nombre', null, 46, 2],
    ['rut', 'RUT', 42, 1],
    ['direccion', 'DIRECCIÓN', 36, 3],
    ['comuna', 'COMUNA', 42, 1],
    ['region', 'REGIÓN', 34, 1],
    ['telefono', 'TELÉFONO', 38, 1],
    ['comentario', 'COMENTARIO', 30, 2],
];

const capacidad = (fuente, maximo) => Math.floor(ANCHO_TEXTO / (fuente * ANCHO_CARACTER)) * maximo - QUIEBRE * (maximo - 1);

const ajustar = (texto, fuente, maximo) => {
    let f = fuente;
    while (f > FUENTE_MIN && texto.length > capacidad(f, maximo)) f -= 1;
    const cap = capacidad(f, maximo);
    return [f, texto.length <= cap ? texto : `${texto.slice(0, Math.max(cap - 1, 1)).trimEnd()}…`];
};

const lineasDe = (texto, fuente, maximo) => {
    const porLinea = Math.max(Math.floor(ANCHO_TEXTO / (fuente * ANCHO_CARACTER_LINEAS)), 1);
    return Math.min(Math.max(Math.ceil(texto.length / porLinea), 1), maximo);
};

const layoutDestinatario = (valores, conPago) => {
    const campos = CAMPOS_PREVIA
        .map(([clave, titulo, fuente, maximo]) => ({ clave, titulo, fuente, maximo, texto: valores[clave] || '' }))
        .filter((c) => c.texto);
    const medir = (escala) => campos.map((c) => {
        const [fuente, texto] = ajustar(c.texto, Math.round(c.fuente * escala), c.maximo);
        return { ...c, fuente, texto };
    });
    const alto = (lista) => lista.reduce((suma, c) => suma + (c.titulo ? 24 : 0)
        + lineasDe(c.texto, c.fuente, c.maximo) * (c.fuente + 4) + 14, 0);

    const disponible = Y_LINEA_BULTO - 10 - (Y_LINEA_DESTINATARIO + 14 + (conPago ? 58 : 34));
    const total = alto(medir(1));
    return medir(total > disponible ? Math.max(disponible / total, ESCALA_MIN) : 1);
};

const CampoPrevia = ({ titulo, texto, fuente, maximo }) => (
    <>
        {titulo && <Typography sx={{ fontSize: 22 * PX_POR_DOT, lineHeight: 1.1 }}>{titulo}</Typography>}
        <Typography sx={{
            fontWeight: 700,
            fontSize: fuente * PX_POR_DOT,
            lineHeight: (fuente + 4) / fuente,
            mb: `${14 * PX_POR_DOT}px`,
            wordBreak: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: maximo,
            flexShrink: 0,
        }}>
            {texto}
        </Typography>
    </>
);

const VistaPreviaEtiqueta = ({ datos, comentario, remitente, pago, pedido, empresa, logoUrl, pie, courier, courierNombre, bulto, total, token, papel }) => {
    const iconoCourier = DEFAULT_DROPOFF_CARRIER_CATALOG.find((c) => c.key === courier)?.iconPath;
    const razon = (papel?.alto || 152.5) / (papel?.ancho || 100);
    const direccionRemitente = [remitente?.direccion, remitente?.comuna].filter(Boolean).join(', ');

    return (
        <Box sx={{
            width: 210,
            minWidth: 210,
            aspectRatio: `1 / ${razon}`,
            border: '2px solid #222',
            borderRadius: 0.5,
            p: 1.2,
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            fontFamily: 'Helvetica, Arial, sans-serif',
            lineHeight: 1.15,
            overflow: 'hidden',
        }}>
            {/* Encabezado: el logo a la izquierda y el remitente a su lado */}
            <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ mb: 0.6 }}>
                <Box sx={{ width: 64, minWidth: 64 }}>
                    {logoUrl ? (
                        <img src={logoUrl} alt={empresa || ''} style={{ maxHeight: 48, maxWidth: 64, objectFit: 'contain' }} />
                    ) : (
                        <Typography sx={{ fontWeight: 800, fontSize: 11 }}>{empresa || 'HAULERS'}</Typography>
                    )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 5.5, letterSpacing: 0.4 }}>REMITENTE</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 8 }}>{remitente?.nombre || empresa}</Typography>
                    {remitente?.rut && <Typography sx={{ fontWeight: 700, fontSize: 6.5 }}>RUT {formatearRut(remitente.rut)}</Typography>}
                    {direccionRemitente && <Typography sx={{ fontWeight: 700, fontSize: 6.5 }}>{direccionRemitente}</Typography>}
                    {remitente?.telefono && <Typography sx={{ fontSize: 5.5 }}>Tel. {formatearTelefono(remitente.telefono)}</Typography>}
                    {remitente?.email && <Typography sx={{ fontSize: 5.5 }} noWrap>{remitente.email}</Typography>}
                </Box>
            </Stack>
            <Divider sx={{ borderColor: '#222', mb: 0.6 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography sx={{ fontSize: 7, letterSpacing: 0.4 }}>DESTINATARIO</Typography>
                {pago && (
                    <Box sx={{ background: '#111', color: 'white', fontWeight: 800, fontSize: 8, px: 0.8, py: 0.2 }}>
                        {PAGOS[pago]}
                    </Box>
                )}
            </Stack>
            {layoutDestinatario({
                ...datos,
                nombre: datos.nombre || '—',
                rut: formatearRut(datos.rut),
                telefono: formatearTelefono(datos.telefono),
                comentario,
            }, !!pago).map((c) => <CampoPrevia key={c.clave} {...c} />)}

            <Box sx={{ flexGrow: 1 }} />
            <Divider sx={{ borderColor: '#222', mb: 0.5 }} />
            <Stack direction="row" spacing={0.8} alignItems="stretch" sx={{ flexShrink: 0 }}>
                {/* Columna 1: QR con su leyenda, los dos centrados en la columna */}
                <Box sx={{ width: 54, minWidth: 54, textAlign: 'center' }}>
                    <QrCode2Icon sx={{ fontSize: 40, display: 'block', mx: 'auto' }} />
                    <Typography sx={{ fontSize: 5.5, lineHeight: 1.25 }}>
                        Escanear<br />al cargar y<br />al entregar
                    </Typography>
                </Box>
                {/* Columna 2: el bulto en una sola fila, y debajo pedido, token y el pie */}
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        BULTO {bulto} DE {total}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 8.5 }}>Pedido #{pedido}</Typography>
                    <Typography sx={{ fontSize: 6, color: '#555' }} noWrap>
                        {token || 'código al imprimir'}
                    </Typography>
                    <Typography sx={{ fontSize: 6, mt: 0.4 }}>{pie}</Typography>
                </Box>
                {/* Columna 3: el logo del courier, centrado en los dos ejes y a la
                    altura del QR, para que las dos columnas laterales se lean parejas */}
                {courier && (
                    <Box sx={{ width: 52, minWidth: 52, textAlign: 'center' }}>
                        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {iconoCourier && (
                                <img src={iconoCourier} alt={courierNombre || courier}
                                     style={{ maxHeight: 26, maxWidth: 50, objectFit: 'contain' }} />
                            )}
                        </Box>
                        <Typography sx={{ fontSize: 5.5, lineHeight: 1.25 }}>
                            Enviado por<br />{courierNombre || courier}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

// Datos del remitente: se configuran una vez y salen en todas las etiquetas de la empresa.
const RemitenteDialog = ({ open, remitente, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState({});
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (open) setForm(_.pick(remitente || {}, CAMPOS_REMITENTE.map((c) => c.key)));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const guardar = async () => {
        setGuardando(true);
        try {
            const data = await orderServices.updateHaulersLabelSender(form);
            enqueueSnackbar('Datos del remitente guardados', { variant: 'success' });
            onSaved(data.remitente);
            onClose();
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.error || 'No se pudieron guardar los datos del remitente', { variant: 'error' });
        }
        setGuardando(false);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Typography variant="h4" color="primary" sx={{ mb: 0 }}>Datos del remitente</Typography>
                <Typography variant="body2" color="text.secondary">
                    Salen arriba en todas las etiquetas de la empresa, junto al logo. Lo que quede vacío no se imprime.
                </Typography>
            </DialogTitle>
            {/* Sin el padding de arriba, la etiqueta flotante del primer campo queda cortada
                bajo el titulo: MUI le saca el padding al contenido que sigue a un DialogTitle. */}
            <DialogContent sx={{ pt: '8px !important' }}>
                <Stack spacing={2}>
                    {CAMPOS_REMITENTE.map(({ key, label }) => (
                        <TextField
                            key={key}
                            size="small"
                            label={label}
                            value={form[key] || ''}
                            onChange={(e) => setForm((prev) => ({
                                ...prev,
                                [key]: key === 'rut' ? formatearRut(e.target.value) : e.target.value,
                            }))}
                        />
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton loading={guardando} variant="contained" size="small" onClick={guardar}>Guardar</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

const HaulersLabelDialog = ({ isOpen, order, onClose, onPrinted }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();
    const [preflight, setPreflight] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    // Lo del destinatario se edita aca y queda solo en el pedido: el cliente no cambia.
    const [nombre, setNombre] = useState('');
    const [rut, setRut] = useState('');
    const [telefono, setTelefono] = useState('');
    const [direccion, setDireccion] = useState('');
    // Nota para quien entrega; sale en la etiqueta y queda guardada en el pedido.
    const [comentario, setComentario] = useState('');
    const [bultos, setBultos] = useState(1);
    const [comuna, setComuna] = useState('');
    const [region, setRegion] = useState('');
    // Pagado / Por pagar: parte sin elegir y hay que marcar uno para imprimir.
    const [pago, setPago] = useState('');
    const [remitente, setRemitente] = useState({});
    const [remitenteOpen, setRemitenteOpen] = useState(false);
    // La comuna que traía el pedido, cuando no calza con ninguna comuna real.
    const [comunaOriginal, setComunaOriginal] = useState(null);
    const [printers, setPrinters] = useState([]);
    const [printerId, setPrinterId] = useState(null);
    const [imprimiendo, setImprimiendo] = useState(false);
    const [descargando, setDescargando] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        setError(null);
        try {
            const [data, resp] = await Promise.all([
                orderServices.getHaulersLabelPreflight(order.id),
                printerServices.getPrinters('?q[active_eq]=true&include=centre&page=1'),
            ]);
            setPreflight(data);
            setNombre(data?.datos?.nombre || '');
            setRut(formatearRut(data?.datos?.rut || ''));
            setTelefono(data?.datos?.telefono || '');
            setDireccion(data?.datos?.direccion || '');
            setComentario(data?.comentario || '');
            setBultos(data?.bultos_sugeridos || 1);
            setPago(data?.pago || '');
            setRemitente(data?.remitente || {});

            // Shopify trae la comuna como la tipeó la persona: "san carlos",
            // "NUNOA", con tildes de más o de menos. Si reconocemos el nombre, la
            // dejamos canónica y deducimos la región; si no calza con ninguna
            // comuna real, la mostramos como aviso para que la elijan a mano.
            const cruda = data?.datos?.comuna || '';
            const canonica = comunaCanonica(cruda);
            setComuna(canonica || '');
            setRegion(canonica ? (regionDeComuna(canonica) || data?.datos?.region || '') : '');
            setComunaOriginal(cruda && !canonica ? cruda : null);
            const lista = resp?.data || [];
            setPrinters(lista);
            const ultima = parseInt(localStorage.getItem('hermes:lastZebraPrinterId'), 10);
            setPrinterId((_.find(lista, { id: ultima }) || lista[0])?.id || null);
        } catch (err) {
            setError(err?.response?.data?.error || 'No se pudieron cargar los datos del pedido.');
        }
        setCargando(false);
    }, [order?.id]);

    useEffect(() => {
        if (isOpen && order?.id) cargar();
        if (!isOpen) { setPreflight(null); setError(null); }
    }, [isOpen, order?.id, cargar]);

    // El RUT ya no es obligatorio: si viene vacío no sale en la etiqueta. Si se escribe,
    // se valida el dígito verificador, que es imposible de corregir una vez impreso.
    const rutOk = !rut.trim() || rutValido(rut);
    const bultosNum = parseInt(bultos, 10);
    const bultosOk = Number.isInteger(bultosNum) && bultosNum >= 1 && bultosNum <= 99;
    const yaEscaneados = preflight?.bultos_escaneados || 0;
    const cambiaBultosConEscaneos = yaEscaneados > 0 && bultosNum !== preflight?.bultos_existentes;
    const printer = _.find(printers, { id: printerId });
    const limites = { ...LIMITES, ...preflight?.limites };
    // Lo que viene del ERP puede pasarse del límite (el maxLength solo frena lo que se
    // tipea): en ese caso se marca el campo y no se deja generar hasta acortarlo.
    const largoOk = (valor, campo) => valor.trim().length <= limites[campo];
    const largosOk = largoOk(nombre, 'nombre') && largoOk(direccion, 'direccion') && largoOk(comentario, 'comentario');

    // El PDF no necesita impresora: el papel por defecto es el 10×15 del layout.
    const puedeGenerar = !cargando && !!preflight && !!nombre.trim() && !!direccion.trim() && !!comuna
        && rutOk && bultosOk && !!pago && !cambiaBultosConEscaneos && largosOk;
    const puedeImprimir = puedeGenerar && !error && !!printer;

    const destinatario = {
        nombre: nombre.trim(),
        rut: rut.trim(),
        telefono: telefono.trim(),
        direccion: direccion.trim(),
        comuna,
        region,
    };

    const payload = {
        bultos: bultosNum,
        pago,
        printer_id: printer?.id,
        destinatario,
        comentario: comentario.trim(),
    };

    // Descargar cuenta igual que imprimir: el backend crea los bultos con su QR y
    // marca el pedido, porque el PDF también se imprime y se escanea.
    const handleDescargarPdf = async () => {
        setDescargando(true);
        setError(null);
        try {
            const data = await orderServices.createHaulersLabel(order.id, { ...payload, formato: 'pdf' });
            const bytes = Uint8Array.from(atob(data.pdf), (c) => c.charCodeAt(0));
            const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `etiqueta-${data.order_code}.pdf`;
            link.click();
            URL.revokeObjectURL(url);

            enqueueSnackbar(`${data.bultos} etiqueta(s) del pedido ${data.order_code} descargadas en PDF`, { variant: 'success' });
            if (onPrinted) onPrinted(data);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || err;
            setError(`No se pudo generar el PDF: ${msg}`);
        }
        setDescargando(false);
    };

    const handleImprimir = async () => {
        setImprimiendo(true);
        setError(null);
        try {
            const data = await orderServices.createHaulersLabel(order.id, payload);

            localStorage.setItem('hermes:lastZebraPrinterId', String(printer.id));
            // Por nombre si el PC la tiene instalada: así la impresión sobrevive
            // a que el DHCP le cambie la IP a la impresora.
            await qzPrintServices.printZpl({
                printerName: printer.printer_name || undefined,
                host: printer.printer_name ? undefined : printer.ip,
                port: printer.port,
                zpl: data.zpl,
            });

            enqueueSnackbar(
                `${data.bultos} etiqueta(s) del pedido ${data.order_code} enviadas a ${printer.name}`,
                { variant: 'success' },
            );
            if (onPrinted) onPrinted(data);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || err;
            setError(`No se pudo imprimir: ${msg}`);
        }
        setImprimiendo(false);
    };

    const previews = bultosOk
        ? _.range(1, Math.min(bultosNum, 6) + 1).map((n) => (
            <VistaPreviaEtiqueta
                key={n}
                datos={destinatario}
                comentario={comentario.trim()}
                remitente={remitente}
                pago={pago}
                pedido={preflight?.order_code}
                empresa={authUser?.current_company?.name}
                // logo_url viene relativo, igual que en el sidebar: hay que
                // prefijarlo con el host de la API o la imagen no carga.
                logoUrl={authUser?.current_company?.logo_url
                    ? `${HOST}${authUser.current_company.logo_url}`
                    : null}
                pie={preflight?.pie || 'Preparado con Haulers · haulers.cl'}
                courier={preflight?.courier}
                courierNombre={preflight?.courier_nombre}
                bulto={n}
                total={bultosNum}
                token={preflight?.tokens?.[n - 1]?.token}
                papel={printer && { ancho: Number(printer.label_width_mm), alto: Number(printer.label_height_mm) }}
            />
        ))
        : [];

    const campo = { fullWidth: true, size: 'small', sx: { mb: 2, background: 'white' } };
    const contador = (valor, clave) => (largoOk(valor, clave)
        ? `${valor.trim().length}/${limites[clave]}`
        : `${valor.trim().length}/${limites[clave]}: no cabe en la etiqueta, acórtalo.`);

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3" color="primary" sx={{ mb: 0 }}>
                        Etiqueta Haulers — pedido {order?.code}
                    </Typography>
                    <Tooltip title="Datos del remitente">
                        <IconButton onClick={() => setRemitenteOpen(true)} disabled={cargando || !preflight}>
                            <SettingsOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {cargando ? (
                    <Stack alignItems="center" my={4}><CircularProgress /></Stack>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            <Typography variant="h5" sx={{ mb: 1.5 }}>Destinatario</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Lo que cambies queda solo en este pedido; el cliente no cambia. Lo que quede vacío no sale en la etiqueta.
                            </Typography>

                            <TextField
                                {...campo}
                                label="Nombre"
                                required
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                error={!nombre.trim() || !largoOk(nombre, 'nombre')}
                                inputProps={{ maxLength: limites.nombre }}
                                helperText={largoOk(nombre, 'nombre') ? undefined : contador(nombre, 'nombre')}
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        {...campo}
                                        label="RUT"
                                        value={rut}
                                        onChange={(e) => setRut(formatearRut(e.target.value))}
                                        error={!rutOk}
                                        helperText={!rutOk ? 'El dígito verificador no calza.' : undefined}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        {...campo}
                                        label="Teléfono"
                                        value={telefono}
                                        onChange={(e) => setTelefono(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                {...campo}
                                label="Dirección"
                                required
                                value={direccion}
                                onChange={(e) => setDireccion(e.target.value)}
                                error={!direccion.trim() || !largoOk(direccion, 'direccion')}
                                placeholder="Calle y número, depto"
                                inputProps={{ maxLength: limites.direccion }}
                                helperText={contador(direccion, 'direccion')}
                            />

                            {comunaOriginal && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    El pedido trae la comuna como <strong>“{comunaOriginal}”</strong>, que no
                                    corresponde a ninguna comuna de Chile. Elígela abajo.
                                </Alert>
                            )}

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        size="small"
                                        options={REGIONES.map((r) => r.region)}
                                        value={region || null}
                                        onChange={(e, v) => {
                                            setRegion(v || '');
                                            // Si la comuna elegida no pertenece a la region nueva, se limpia
                                            if (v && comuna && regionDeComuna(comuna) !== v) setComuna('');
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Región" sx={{ background: 'white' }} />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        size="small"
                                        options={region
                                            ? (REGIONES.find((r) => r.region === region)?.comunas || [])
                                            : COMUNAS.map((c) => c.comuna)}
                                        value={comuna || null}
                                        onChange={(e, v) => {
                                            setComuna(v || '');
                                            // La comuna determina la region sin ambiguedad: ningun nombre
                                            // de comuna se repite entre regiones.
                                            if (v) setRegion(regionDeComuna(v) || region);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Comuna"
                                                required
                                                error={!comuna}
                                                sx={{ background: 'white' }}
                                                helperText={comuna ? undefined : 'Elige la comuna: es lo que usa el conductor.'}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                {...campo}
                                label="Comentario"
                                multiline
                                minRows={2}
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                                error={!largoOk(comentario, 'comentario')}
                                placeholder="Ej: dejar en conserjería, frágil"
                                inputProps={{ maxLength: limites.comentario }}
                                helperText={`Sale al final de los datos del destinatario. ${contador(comentario, 'comentario')}`}
                            />

                            <TextField
                                {...campo}
                                type="number" label="Cantidad de bultos"
                                value={bultos}
                                onChange={(e) => setBultos(e.target.value)}
                                inputProps={{ min: 1, max: 99 }}
                                error={!bultosOk}
                                helperText={bultosOk
                                    ? `Sale una etiqueta por bulto, cada una con su propio código.`
                                    : 'Tiene que ser un número entre 1 y 99.'}
                            />

                            <FormControl error={!pago} sx={{ mb: 2 }}>
                                <FormLabel sx={{ fontSize: 13 }}>Pago del envío *</FormLabel>
                                <RadioGroup row value={pago} onChange={(e) => setPago(e.target.value)}>
                                    {Object.entries(PAGOS).map(([value]) => (
                                        <FormControlLabel key={value} value={value} control={<Radio size="small" />}
                                                          label={value === 'pagado' ? 'Pagado' : 'Por pagar'} />
                                    ))}
                                </RadioGroup>
                                {!pago && <FormHelperText sx={{ ml: 0 }}>Elige uno: sale destacado en la etiqueta.</FormHelperText>}
                            </FormControl>

                            <Autocomplete
                                size="small"
                                options={printers}
                                getOptionLabel={(p) => `${p.name}${p.printer_name ? ` — ${p.printer_name}` : ` (${p.ip}:${p.port})`}`}
                                value={printer || null}
                                onChange={(e, v) => setPrinterId(v?.id || null)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Impresora"
                                        sx={{ background: 'white' }}
                                        helperText={printer
                                            ? `Papel ${printer.label_width_mm} × ${printer.label_height_mm} mm a ${printer.dpi} dpi`
                                            : 'Carga una impresora en el mantenedor de Impresoras.'}
                                    />
                                )}
                            />

                            {preflight?.bultos_existentes > 0 && (
                                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                                    <Chip size="small" label={`Ya impreso: ${preflight.bultos_existentes} bulto(s)`} />
                                    {yaEscaneados > 0 && <Chip size="small" color="success" label={`${yaEscaneados} escaneado(s)`} />}
                                </Stack>
                            )}

                            {cambiaBultosConEscaneos && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    Este pedido ya tiene {yaEscaneados} bulto(s) escaneado(s), así que no se puede cambiar
                                    la cantidad a {bultosNum}. Reimprime con {preflight.bultos_existentes} o anula la entrega.
                                </Alert>
                            )}

                            {preflight?.bultos_existentes > 0 && !cambiaBultosConEscaneos
                                && bultosNum === preflight.bultos_existentes && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Reimpresión: se reusan los mismos códigos, las etiquetas viejas siguen sirviendo.
                                </Alert>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                Vista previa {bultosOk && bultosNum > 1 ? `(${bultosNum} etiquetas)` : ''}
                            </Typography>
                            <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                                {previews}
                            </Stack>
                            {bultosNum > 6 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Se muestran las primeras 6 de {bultosNum}.
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                El QR se dibuja en la impresora, por eso acá va representado con un ícono.
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton
                    loading={descargando}
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfOutlinedIcon />}
                    disabled={!puedeGenerar || imprimiendo}
                    onClick={handleDescargarPdf}>
                    Descargar PDF
                </LoadingButton>
                <LoadingButton
                    loading={imprimiendo}
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PrintOutlinedIcon />}
                    disabled={!puedeImprimir || descargando}
                    onClick={handleImprimir}>
                    {bultosOk && bultosNum > 1 ? `Imprimir ${bultosNum} etiquetas` : 'Imprimir etiqueta'}
                </LoadingButton>
            </DialogActions>

            <RemitenteDialog
                open={remitenteOpen}
                remitente={remitente}
                onClose={() => setRemitenteOpen(false)}
                onSaved={setRemitente}
            />
        </Dialog>
    );
};

export default HaulersLabelDialog;
